import discountQueries from '@apis/queries/discountQueries';
import Button from '@components/Button';
import Chip from '@components/Chip';
import Column from '@components/Column';
import CtaLayout from '@components/CtaLayout';
import Description from '@components/Description';
import Header from '@components/Header';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import useToast from '@hooks/ui/useToast';
import Info from '@icons/info/Info';
import NoticeIcon from '@icons/Notice';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemCouponTarget, itemCouponDraftAtom } from './atoms';
import ItemCouponList from './components/ItemCouponList';
import OrderCouponList from './components/OrderCouponList';
import useApplyCoupons from './hooks/useApplyCoupons';

declare module '@stackflow/config' {
    interface Register {
        OrderCouponActivity: {};
    }
}

type CouponTab = 'ITEM' | 'ORDER';

/** 결제 화면의 쿠폰 선택. 상품 쿠폰과 장바구니(주문) 쿠폰 탭으로 나뉜다 */
const OrderCouponActivity: ActivityComponentType<'OrderCouponActivity'> = () => {
    const { t } = useTranslation('order');
    const priceUnit = usePriceUnit();
    const toast = useToast();
    const { discounts, shopId } = useCalculatedRequest();
    const { data } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });
    const initialOrderCouponId = discounts
        ?.find((it) => it.type === 'COUPON' && it.discountScope === 'ORDER')?.couponId ?? null;
    const [tab, setTab] = useState<CouponTab>(initialOrderCouponId ? 'ORDER' : 'ITEM');
    const [selectedOrderCouponId, setSelectedOrderCouponId] = useState<string | null>(initialOrderCouponId);
    const [draft, setDraft] = useAtom(itemCouponDraftAtom);
    const {
        getItemDiscounts, getOrderDiscountAmount, applyItemCoupons, applyOrderCoupon, clearCoupons,
    } = useApplyCoupons();

    // 적용 중인 상품 쿠폰으로 초안을 시작하고, 페이지를 벗어나면 비운다
    useEffect(() => {
        const initial: Record<string, ItemCouponTarget> = {};
        discounts
            ?.filter((it) => it.type === 'COUPON' && it.discountScope === 'ITEM' && it.couponId && it.itemKey)
            .forEach((it) => {
                const unit = it.idx ?? 0;
                initial[it.couponId!] = {
                    itemKey: it.itemKey!,
                    units: it.discountMethod === 'BOGO' ? [unit, unit + 1] : [unit],
                };
            });
        setDraft(initial);
        return () => setDraft({});
    }, []);

    // 상품 쿠폰과 장바구니 쿠폰은 함께 쓸 수 없다. 상품 쿠폰을 고르는 즉시 장바구니 쿠폰을 해제한다
    const hasItemCoupon = Object.keys(draft).length > 0;
    useEffect(() => {
        if (hasItemCoupon && selectedOrderCouponId) {
            setSelectedOrderCouponId(null);
            toast.info(t('couponPage.orderCouponReleased'));
        }
    }, [hasItemCoupon]);

    const toggleOrderCoupon = (couponId: string) => {
        if (selectedOrderCouponId === couponId) {
            setSelectedOrderCouponId(null);
            return;
        }
        // 장바구니 쿠폰을 고르는 즉시 상품 쿠폰을 해제한다
        if (hasItemCoupon) {
            setDraft({});
            toast.info(t('couponPage.itemCouponReleased'));
        }
        setSelectedOrderCouponId(couponId);
    };

    const selectedOrderCoupon = data?.coupons.find((it) => it.id === selectedOrderCouponId);
    /*
    const discountAmount = selectedOrderCoupon
        ? getOrderDiscountAmount(selectedOrderCoupon)
        : getItemDiscounts(draft).reduce((acc, cur) => acc + cur.discountAmount, 0);

     */

    // 둘 중 하나만 선택되어 있으므로 탭과 상관없이 선택된 쪽을 적용한다
    const handleApply = () => {
        if (selectedOrderCoupon) {
            applyOrderCoupon(selectedOrderCoupon);
        } else if (hasItemCoupon) {
            applyItemCoupons(draft);
        } else {
            clearCoupons();
        }
    };

    return (
        <AppScreen>
            <CtaLayout header={<Header title={t('couponPage.title')} />}
                noFooterBorder
                footer={(
                    <Button fullWidth
                        onClick={handleApply}
                        id='apply-coupon-button' >
                        {t('couponPage.apply')}
                    </Button>
                )} >
                <Column fullWidth>
                    <Tabs>
                        <Chip.Container variant='fill'>
                            <Chip isSelected={tab === 'ITEM'}
                                onClick={() => setTab('ITEM')}
                                aria-label='coupon-chip'
                                data-coupon-scope='ITEM' >
                                {t('couponPage.itemTab')}
                            </Chip>
                            <Chip isSelected={tab === 'ORDER'}
                                onClick={() => setTab('ORDER')}
                                aria-label='coupon-chip'
                                data-coupon-scope='ORDER' >
                                {t('couponPage.orderTab')}
                            </Chip>
                        </Chip.Container>
                    </Tabs>

                    <NoticeWrapper>
                        <Notice>
                            <NoticeIcon size={16} color={colors.primary}/>
                            {t('couponPage.notice')}
                        </Notice>
                    </NoticeWrapper>
                    <List>
                        {tab === 'ITEM' ? (
                            <ItemCouponList />
                        ) : (
                            <OrderCouponList selectedCouponId={selectedOrderCouponId}
                                onToggle={toggleOrderCoupon} />
                        )}
                    </List>
                </Column>
            </CtaLayout>
        </AppScreen>
    );
};

const Tabs = styled.div`
    padding: var(--spacing-3) var(--spacing-4);
    width: 100%;
`;

const NoticeWrapper = styled.div`
    padding: var(--spacing-2) var(--spacing-4);
    width: 100%;
`;

const Notice = styled(Description)`
    padding: var(--spacing-4);
`;

const List = styled.div`
    padding: var(--spacing-2) var(--spacing-4) var(--spacing-4);
    width: 100%;
`;

export default OrderCouponActivity;
